import { CHAT_MAX_UPLOAD_BYTES } from "@/lib/chatApi";

export type ChatPhotoFilterId = "original" | "clair" | "film" | "noir" | "chrome";

export type ChatPhotoFilter = {
  id: ChatPhotoFilterId;
  label: string;
  css: string;
  overlay?: string;
  blend?: GlobalCompositeOperation;
};

export const CHAT_PHOTO_FILTERS: ChatPhotoFilter[] = [
  { id: "original", label: "Original", css: "none" },
  {
    id: "clair",
    label: "Clair",
    css: "brightness(1.32) contrast(1.28) saturate(1.55)",
    overlay: "rgba(200, 228, 255, 0.46)",
    blend: "soft-light",
  },
  {
    id: "film",
    label: "Film",
    css: "sepia(0.5) contrast(1.4) saturate(1.55) brightness(1.05)",
    overlay: "rgba(255, 92, 24, 0.42)",
    blend: "soft-light",
  },
  {
    id: "noir",
    label: "Noir",
    css: "grayscale(1) contrast(1.28) brightness(0.96)",
  },
  {
    id: "chrome",
    label: "Chrome",
    css: "contrast(1.58) saturate(0.78) brightness(1.12)",
    overlay: "rgba(16, 68, 180, 0.52)",
    blend: "soft-light",
  },
];

const IMAGE_EXT = /\.(gif|png|jpe?g|webp|heic|heif|avif)$/i;
const STILL_EXT = /\.(png|jpe?g|webp|heic|heif|avif)$/i;
const MAX_BAKE_EDGE = 2560;

export function isComposerImageFile(file: File): boolean {
  const mime = (file.type || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return IMAGE_EXT.test(file.name);
}

/** Photo fixe à filtrer (pas GIF / SVG). */
export function isFilterableChatPhoto(file: File): boolean {
  const mime = (file.type || "").toLowerCase();
  if (mime === "image/gif" || mime === "image/svg+xml") return false;
  if (mime.startsWith("image/")) return true;
  return STILL_EXT.test(file.name);
}

export function chatPhotoFilterById(id: ChatPhotoFilterId): ChatPhotoFilter {
  return CHAT_PHOTO_FILTERS.find((f) => f.id === id) ?? CHAT_PHOTO_FILTERS[0];
}

async function decodePhoto(file: File): Promise<CanvasImageSource> {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, {
        imageOrientation: "from-image",
      } as ImageBitmapOptions);
    } catch {
      /* HEIC / vieux moteurs */
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode"));
    };
    img.src = url;
  });
}

function sourceSize(src: CanvasImageSource): { w: number; h: number } {
  if (src instanceof HTMLImageElement) {
    return { w: src.naturalWidth || src.width, h: src.naturalHeight || src.height };
  }
  if (typeof ImageBitmap !== "undefined" && src instanceof ImageBitmap) {
    return { w: src.width, h: src.height };
  }
  const anySrc = src as { width: number; height: number };
  return { w: anySrc.width, h: anySrc.height };
}

function jpegName(name: string): string {
  return name.replace(/\.[a-z0-9]+$/i, "") + "-filtre.jpg";
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("blob"));
      },
      "image/jpeg",
      quality
    );
  });
}

/** Original inchangé. Sinon JPEG filtré. Échec → fichier d’origine. */
export async function bakeChatPhotoFilter(file: File, filterId: ChatPhotoFilterId): Promise<File> {
  const filter = chatPhotoFilterById(filterId);
  if (filter.id === "original") return file;

  try {
    const src = await decodePhoto(file);
    let { w, h } = sourceSize(src);
    if (!w || !h) return file;
    if (Math.max(w, h) > MAX_BAKE_EDGE) {
      const scale = MAX_BAKE_EDGE / Math.max(w, h);
      w = Math.max(1, Math.round(w * scale));
      h = Math.max(1, Math.round(h * scale));
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.filter = filter.css === "none" ? "none" : filter.css;
    ctx.drawImage(src, 0, 0, w, h);
    ctx.filter = "none";
    if (filter.overlay) {
      ctx.globalCompositeOperation = filter.blend ?? "soft-light";
      ctx.fillStyle = filter.overlay;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "source-over";
    }
    if ("close" in src && typeof (src as ImageBitmap).close === "function") {
      try {
        (src as ImageBitmap).close();
      } catch {
        /* ignore */
      }
    }
    let quality = 0.9;
    let blob = await canvasToBlob(canvas, quality);
    if (blob.size > CHAT_MAX_UPLOAD_BYTES) {
      quality = 0.78;
      blob = await canvasToBlob(canvas, quality);
    }
    if (blob.size > CHAT_MAX_UPLOAD_BYTES) {
      blob = await canvasToBlob(canvas, 0.62);
    }
    if (blob.size > CHAT_MAX_UPLOAD_BYTES) return file;
    return new File([blob], jpegName(file.name || "photo.jpg"), { type: "image/jpeg" });
  } catch {
    return file;
  }
}
